import { useRef, useState } from "react";
import { type Bucket } from "../../shared/types.ts";
import { api } from "../api.ts";
import { Banner, BucketPicker } from "../components.tsx";
import { navigate } from "../useRoute.ts";

const COMMON_AREAS = ["yard", "house", "garage", "kitchen", "basement"];

/**
 * The whole point of this screen is speed: type a title, optionally snap a
 * photo, hit save. Everything else can be filled in later on the detail page.
 */
export function QuickAdd() {
  const [title, setTitle] = useState("");
  const [area, setArea] = useState("");
  const [bucket, setBucket] = useState<Bucket>("season");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!title.trim() || busy) return;

    setBusy(true);
    setError(null);
    try {
      const project = await api.createProject({ title: title.trim(), area: area.trim(), bucket });

      if (file) {
        // A failed photo shouldn't lose the project — report and move on.
        try {
          await api.uploadPhoto(project.id, file);
        } catch (err) {
          setError(`Project saved, but the photo failed: ${err instanceof Error ? err.message : err}`);
          setBusy(false);
          navigate(`/p/${project.id}`);
          return;
        }
      }

      navigate(`/p/${project.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
      setBusy(false);
    }
  }

  return (
    <form className="quick-add" onSubmit={submit}>
      <h2>Add a project</h2>
      {error && <Banner kind="error">{error}</Banner>}

      <label htmlFor="title">What needs doing?</label>
      <input
        id="title"
        className="big-input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Re-stain the deck"
        autoFocus
        enterKeyHint="done"
        required
      />

      <label>Where?</label>
      <div className="chips">
        {COMMON_AREAS.map((option) => (
          <button
            key={option}
            type="button"
            className={`chip ${area === option ? "chip-on" : ""}`}
            onClick={() => setArea(area === option ? "" : option)}
          >
            {option}
          </button>
        ))}
      </div>
      <input
        aria-label="Custom area"
        value={area}
        onChange={(e) => setArea(e.target.value)}
        placeholder="or type an area"
      />

      <label>When?</label>
      <BucketPicker value={bucket} onChange={setBucket} legend="When" />

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
      />
      <button type="button" className="secondary big" onClick={() => fileInput.current?.click()}>
        {file ? `📷 ${file.name}` : "📷 Add a photo"}
      </button>
      {file && (
        <button type="button" className="link" onClick={() => setFile(null)}>
          Remove photo
        </button>
      )}

      <button type="submit" className="primary big" disabled={busy || !title.trim()}>
        {busy ? "Saving…" : "Save project"}
      </button>

      <p className="muted small pad">
        Adding a bunch at once? <a href="#/bulk-add">Bulk add →</a>
      </p>
    </form>
  );
}
